<template>
  <div class="pricing-page">
    <!-- Logo -->
    <div class="text-center pt-8 pb-2">
      <v-img
        src="/img/fantasy_lineups_logo.png"
        class="logo mx-auto"
        width="350"
        contain
      />
    </div>

    <!-- Heading -->
    <div class="text-center mb-2">
      <h1 class="white--text text-h4 text-md-h3 font-weight-bold">
        Choose Your Plan
      </h1>
      <p class="grey--text text--lighten-1 text-body-1 mt-2 mb-1">
        Everything Included No tiers No locked tools
      </p>
      <p class="green--text text-h6 font-weight-bold mb-1">
        NFL &nbsp;&bull;&nbsp; NBA &nbsp;&bull;&nbsp; MLB &nbsp;&bull;&nbsp; NHL
      </p>
      <p class="grey--text text--lighten-1 text-body-1">
        Cancel anytime No contracts
      </p>
    </div>

    <!-- What's Included Box -->
    <v-container class="px-4" style="max-width: 900px;">
      <div class="features-box pa-6 mb-8">
        <h2 class="white--text text-h6 text-md-h5 font-weight-bold text-center mb-5">
          What's Included (All Plans)
        </h2>
        <v-row>
          <v-col cols="12" sm="6">
            <div
              v-for="(feature, i) in includedFeaturesLeft"
              :key="'left-' + i"
              class="d-flex align-center mb-3"
            >
              <v-icon color="green" class="mr-3" small>
                mdi-check-circle
              </v-icon>
              <span class="white--text text-body-2 text-sm-body-1">{{ feature }}</span>
            </div>
          </v-col>
          <v-col cols="12" sm="6">
            <div
              v-for="(feature, i) in includedFeaturesRight"
              :key="'right-' + i"
              class="d-flex align-center mb-3"
            >
              <v-icon color="green" class="mr-3" small>
                mdi-check-circle
              </v-icon>
              <span class="white--text text-body-2 text-sm-body-1">{{ feature }}</span>
            </div>
          </v-col>
        </v-row>
      </div>

      <!-- Pricing Cards -->
      <v-row justify="center" class="mb-6">
        <!-- Weekly -->
        <v-col cols="12" sm="4" class="d-flex">
          <div class="price-card pa-6 text-center flex-grow-1">
            <h3 class="white--text text-h6 text-uppercase font-weight-bold mb-4">
              Weekly
            </h3>
            <p class="green--text mb-1">
              <span class="text-body-1">$</span>
              <span class="price-large">3</span><span class="price-decimal">.99</span>
              <span class="grey--text text--lighten-1 text-body-2">/week</span>
            </p>
            <v-btn
              rounded
              depressed
              color="#73c604"
              class="white--text mt-4 subscribe-btn"
              :disabled="resuming"
              :loading="resuming"
              @click.prevent="platinumModel(0)"
            >
              Subscribe
            </v-btn>
          </div>
        </v-col>

        <!-- Monthly -->
        <v-col cols="12" sm="4" class="d-flex">
          <div class="price-card price-card--deal pa-6 text-center flex-grow-1">
            <span class="deal-badge">Limited time</span>
            <h3 class="white--text text-h6 text-uppercase font-weight-bold mb-4">
              Monthly
            </h3>
            <p class="green--text mb-1">
              <del class="was-price">$9.99</del>
              <span class="text-body-1">$</span>
              <span class="price-large">4</span><span class="price-decimal">.99</span>
              <span class="grey--text text--lighten-1 text-body-2 deal-period">first month</span>
            </p>
            <p class="renews-note mb-0">then $9.99/month</p>
            <v-btn
              rounded
              depressed
              color="#73c604"
              class="white--text mt-4 subscribe-btn"
              :disabled="resuming"
              :loading="resuming"
              @click="platinumModel(1)"
            >
              Subscribe
            </v-btn>
          </div>
        </v-col>

        <!-- Yearly (Most Popular) -->
        <v-col cols="12" sm="4" class="d-flex">
          <div class="price-card price-card--popular pa-6 text-center flex-grow-1">
            <span class="popular-badge">Most Popular</span>
            <h3 class="green--text text-h6 text-uppercase font-weight-bold mb-4">
              Yearly
            </h3>
            <p class="green--text mb-1">
              <span class="text-body-1">$</span>
              <span class="price-large">99</span><span class="price-decimal">.99</span>
              <span class="grey--text text--lighten-1 text-body-2">/year</span>
            </p>
            <v-btn
              rounded
              depressed
              color="#73c604"
              class="white--text mt-4 subscribe-btn"
              :disabled="resuming"
              :loading="resuming"
              @click="platinumModel(2)"
            >
              Subscribe
            </v-btn>
          </div>
        </v-col>
      </v-row>

      <!-- Bottom CTA -->
      <div class="text-center pb-8">
        <p class="grey--text text--lighten-1 text-body-1 mb-2">
          Used daily by DFS players across
          <span class="white--text font-weight-bold">NFL</span>,
          <span class="white--text font-weight-bold">NBA</span>,
          <span class="white--text font-weight-bold">MLB</span>, and
          <span class="white--text font-weight-bold">NHL</span>
        </p>
        <p class="text-body-1 mb-4">
          <span class="grey--text text--lighten-1">Already a member?</span>
          <router-link
            :to="{ name: 'loginForm' }"
            class="green--text font-weight-bold ml-1"
          >
            Log in
          </router-link>
        </p>
        <!-- <v-btn
          v-if="!isLoggedIn"
          large
          color="#73c604"
          class="white--text"
          :to="{ name: 'SignUpForm' }"
        >
          Create Free Account
        </v-btn> -->
      </div>
    </v-container>

    <!-- Sign-up prompt snackbar -->
    <v-snackbar
      v-model="showSignupPrompt"
      :timeout="6000"
      color="primary"
      bottom
      multi-line
    >
      Please create a free account or log in before selecting a plan.
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="goToSignUp"
        >
          Sign Up
        </v-btn>
        <v-btn
          text
          v-bind="attrs"
          @click="showSignupPrompt = false"
        >
          Dismiss
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>
<script>
  import { mapState } from 'vuex';
  import UserRegister from './UserRegister.vue';

  export default {
    name: 'pricing',
    title: 'Fantasy Lineups Pricing Page',
    data() {
      return {
        toggle_exclusive: 0,
        sign_up_payload: {},
        showSignupPrompt: false,
        resuming: false,
        includedFeaturesLeft: [
          'Full Optimizer Access',
          'Projections & Insights',
          'Unlimited Builds',
          'Depth Charts & News',
          'NFL Player Rankings (Season)',
          'NFL Weekly Rankings',
        ],
        includedFeaturesRight: [
          'Top Plays',
          'Prop Analyzer',
          'Export Lineups (CSV)',
          'Min/Max Exposure',
          'Breaking News',
          'Player Analysis by RotoBaller',
          'And Much More'
        ],
        features: [
          {
            title: 'Player Projections',
            detail: 'Live Up to the Minute Player Projections,',
            secondaryDetail: 'You can also change the projection yourself.',
          },
          {
            title: 'Fantasy Lineups DFS Optimizer',
            detail: 'NBA, MLB, NHL, NFL',
          },
          {
            title: 'Min/Max Exposure',
            detail: 'Set Minimum & Maximum Player Exposure',
          },
          { title: 'Team Stacks', detail: 'Team Exposures in an Instant' },
          {
            title: 'Expert "Top plays" filters',
            detail: 'Premium Insights Giving you an Edge',
          },
          {
            title: 'Mass Lineup Creation',
            detail: 'Build Hundreds of Lineups in Second',
          },
          { title: 'Deep Insights', detail: 'Detailed Team & Player Insights' },
          {
            title: 'Export Lineups',
            detail: 'Export Lineups into Draftkings & Fanduel',
          },
          {
            title: 'Easy to use Interface',
            detail: 'Modern Simple Straight forward',
          },
        ],
        priceModelFree: [
          {
            title: 'Access to NFL,NHL,NBA,MLB',
            isAvailable: true,
          },
          {
            title: 'Export Lineups Into CSV',
            isAvailable: true,
          },
          {
            title: 'Max Lineups per build',
            isAvailable: true,
            subtitle: '3',
          },
          {
            title: 'Number of Builds',
            isAvailable: true,
            subtitle: '1',
          },
          {
            title: 'Insights',
            isAvailable: true,
          },
          {
            title: 'Access to Our Projections',
            isAvailable: false,
          },
          {
            title: 'Team Stack',
            isAvailable: false,
          },
          {
            title: 'Min/Max Exposure',
            isAvailable: false,
          },
          {
            title: 'Import / Export Projections',
            isAvailable: false,
          },
          {
            title: 'Prop Analyzer',
            isAvailable: false,
          },
          {
            title: 'Top Plays',
            isAvailable: false,
          },
        ],
        priceModelPremium: [
          {
            title: 'Access to NFL,NHL,NBA,MLB',
            isAvailable: true,
          },
          {
            title: 'Export Lineups Into CSV',
            isAvailable: true,
          },
          {
            title: 'Max Lineups per build',
            isAvailable: true,
            subtitle: '150',
          },
          {
            title: 'Number of Builds',
            isAvailable: true,
            subtitle: 'Unlimited',
          },
          {
            title: 'Insights',
            isAvailable: true,
          },
          {
            title: 'Access to Our Projections',
            isAvailable: true,
          },
          {
            title: 'Team Stack',
            isAvailable: true,
          },
          {
            title: 'Min/Max Exposure',
            isAvailable: true,
          },
          {
            title: 'Import / Export Projections',
            isAvailable: true,
          },
          {
            title: 'Prop Analyzer',
            isAvailable: true,
          },
          {
            title: 'Top Plays',
            isAvailable: false,
          },
        ],
        priceModelPlatinum: [
          {
            title: 'Access to NFL,NHL,NBA,MLB',
            isAvailable: true,
          },
          {
            title: 'Fantasy news and analysis',
            isAvailable: true,
          },
          {
            title: 'Weekly rankings',
            isAvailable: true,
          },
          {
            title: 'Depth charts',
            isAvailable: true,
          },
          {
            title: 'Export Lineups Into CSV',
            isAvailable: true,
          },
          {
            title: 'Max Lineups per build',
            isAvailable: true,
            subtitle: '150',
          },
          {
            title: 'Number of Builds',
            isAvailable: true,
            subtitle: 'Unlimited',
          },
          {
            title: 'Insights',
            isAvailable: true,
          },
          {
            title: 'Access to Our Projections',
            isAvailable: true,
          },
          {
            title: 'Team Stack',
            isAvailable: true,
          },
          {
            title: 'Min/Max Exposure',
            isAvailable: true,
          },
          {
            title: 'Import / Export Projections',
            isAvailable: true,
          },
          {
            title: 'Prop Analyzer',
            isAvailable: true,
          },
          {
            title: 'Top Plays',
            isAvailable: true,
          },
        ],
      };
    },
    computed: {
      ...mapState('NbaModule', ['customerSession', 'portal']),
      ...mapState('userModule', ['userStatus', 'sessionWOTrialData']),
      // same key the router guard treats as "signed in" (router/index.js)
      isLoggedIn() {
        return !!localStorage.getItem('user');
      },
      computedHeight() {
        switch (this.$vuetify.breakpoint.name) {
          case 'xs':
            return 2500;
          default:
            return 1050;
        }
      },
    },
    beforeRouteEnter(to, from, next) {
      if (localStorage.getItem('status') === 'active') {
        next({ name: 'NbaLayout' });
      } else {
        next();
      }
    },
    mounted() {
      this.GoogleAnalytic();
      this.GoogleAds();

      // resume a plan chosen before sign-up. consume the param first, or browser-back
      // from Stripe re-fires this and the plan can never be changed.
      // whitelist the raw string: the price ternary below falls through to YEARLY on
      // anything that isn't 0 or 1, and Number('') is 0 — so a mangled or empty
      // ?plan= would otherwise check the user out on a plan they never picked.
      const raw = String(this.$route.query.plan);
      if (['0', '1', '2'].includes(raw)) {
        const query = Object.assign({}, this.$route.query);
        delete query.plan;
        this.$router.replace({ query });
        // checkout is two round-trips away and the cards are already clickable —
        // lock them or a click here opens a second session racing this one
        this.resuming = true;
        this.platinumModel(Number(raw));
      }
    },
    components: {
      UserRegister,
    },
    methods: {
      GoogleAnalytic() {
        this.$gtag.config('UA-204063830-2');
        this.$gtag.pageview({ page_path: '/pricing' });
      },
      GoogleAds() {
        this.$gtag.config('AW-323506423');
      },

      freeModel() {
        const utmSource = this.$route.query.utm_source || 'unknown';
        const utmMedium = this.$route.query.utm_medium || 'unknown';
        const utmCampaign = this.$route.query.utm_campaign || 'unknown';
        const utmTerm = this.$route.query.utm_term || 'unknown';
        const utmContent = this.$route.query.utm_content || 'unknown';
        const priceID =
          this.toggle_exclusive === 0
            ? process.env.VUE_APP_FREE_WEEKLY_PRICE
            : this.toggle_exclusive === 1
            ? process.env.VUE_APP_FREE_MONTHLY_PRICE
            : process.env.VUE_APP_FREE_YEARLY_PRICE;
        if (!localStorage.getItem('stripe_customer_id')) {
          this.$router.push({
            name: 'SignUpForm',
            params: { price_id: priceID, planType: 'Free' },
            query: {
              utm_source: utmSource,
              utm_medium: utmMedium,
              utm_campaign: utmCampaign,
              utm_term: utmTerm,
              utm_content: utmContent,
            },
          });
        }
      },
      premiumModel() {
        const utmSource = this.$route.query.utm_source || 'unknown';
        const utmMedium = this.$route.query.utm_medium || 'unknown';
        const utmCampaign = this.$route.query.utm_campaign || 'unknown';
        const utmTerm = this.$route.query.utm_term || 'unknown';
        const utmContent = this.$route.query.utm_content || 'unknown';
        const priceID =
          this.toggle_exclusive === 0
            ? process.env.VUE_APP_PREMIUM_WEEKLY_PRICE
            : this.toggle_exclusive === 1
            ? process.env.VUE_APP_PREMIUM_MONTHLY_PRICE
            : process.env.VUE_APP_PREMIUM_YEARLY_PRICE;
        if (!localStorage.getItem('stripe_customer_id')) {
          this.$router.push({
            name: 'SignUpForm',
            params: { price_id: priceID },
            query: {
              utm_source: utmSource,
              utm_medium: utmMedium,
              utm_campaign: utmCampaign,
              utm_term: utmTerm,
              utm_content: utmContent,
            },
          });
        } else {
          //? check for user status...
          this.$store
            .dispatch('userModule/getUserStatus', {
              userId: localStorage.getItem('user'),
            })
            .then(() => {
              let obj = {
                stripe_customer_id: localStorage.getItem('stripe_customer_id'),
                price_id: priceID,
                type: utmSource,
              };
              if (this.userStatus.data.istrial == '1') {
                // call without trial api....
                this.$store
                  .dispatch('userModule/createSeesionWithoutTrial', obj)
                  .then(() => {
                    let path = this.sessionWOTrialData.session['url'];
                    console.log('sessionWOTrialData', path);
                    location.href = path;
                  });
              } else {
                this.$store
                  .dispatch('NbaModule/createSession', obj)
                  .then(() => {
                    //* if alreday subscrided...
                    // if (this.customerSession.length == 0) {
                    //   this.goToPortal();
                    // } else {
                    let path = this.customerSession.session['url'];
                    console.log('customerSession', path);
                    location.href = path;
                    // }
                  });
              }
            });
        }
      },
      goToSignUp() {
        this.showSignupPrompt = false;
        this.$router.push({ name: 'SignUpForm' });
      },
      ProceedPayment(priceId) {
        if (!localStorage.getItem('stripe_customer_id')) {
          this.showSignupPrompt = true;
          return;
        }

        const utmSource = this.$route.query.utm_source || 'unknown';

        let obj = {
          stripe_customer_id: localStorage.getItem('stripe_customer_id'),
          price_id: priceId,
          type: utmSource,
        };
        this.$store.dispatch('NbaModule/createSession', obj).then(() => {
          location.href = this.customerSession.session['url'];
        });
      },
      platinumModel(type) {
        const utmSource = this.$route.query.utm_source || 'unknown';
        const utmMedium = this.$route.query.utm_medium || 'unknown';
        const utmCampaign = this.$route.query.utm_campaign || 'unknown';
        const utmTerm = this.$route.query.utm_term || 'unknown';
        const utmContent = this.$route.query.utm_content || 'unknown';
        const priceID =
          type === 0
            ? process.env.VUE_APP_PLATINUM_WEEKLY_PRICE
            : type === 1
            ? process.env.VUE_APP_PLATINUM_MONTHLY_INTRO_PRICE
            : process.env.VUE_APP_PLATINUM_YEARLY_PRICE;

        // treat the string 'undefined' as missing: SET_USER_DATA writes the field
        // unconditionally, so an absent id lands here as a truthy "undefined"
        const customerId = localStorage.getItem('stripe_customer_id');
        if (!customerId || customerId === 'undefined') {
          if (localStorage.getItem('user')) {
            // already registered, just missing a usable stripe id — sending them to
            // sign-up dead-ends on the duplicate email, so prompt to log in instead
            this.resuming = false;
            this.showSignupPrompt = true;
            return;
          }
          // carry the chosen plan through sign-up so it isn't picked twice
          this.$router.push({
            name: 'SignUpForm',
            query: Object.assign({}, this.$route.query, { plan: type }),
          });
          return;
        } else {
          //? check for user status...
          this.$store
            .dispatch('userModule/getUserStatus', {
              userId: localStorage.getItem('user'),
            })
            .then(() => {
              let obj = {
                stripe_customer_id: localStorage.getItem('stripe_customer_id'),
                price_id: priceID,
                type: utmSource,
              };
              if (this.userStatus.data.istrial == '1') {
                // call without trial api…
                this.$store
                  .dispatch('userModule/createSeesionWithoutTrial', obj)
                  .then(() => {
                    let path = this.sessionWOTrialData.session['url'];
                    console.log('sessionWOTrialData', path);
                    location.href = path;
                  });
              } else {
                this.$store
                  .dispatch('NbaModule/createSession', obj)
                  .then(() => {
                    //* if already subscribed...
                    if (this.customerSession.length == 0) {
                      this.goToPortal();
                    } else {
                      let path = this.customerSession.session['url'];
                      console.log('customerSession', path);
                      location.href = path;
                    }
                  });
              }
            });
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .pricing-page {
    min-height: 100vh;
    background: rgba(0, 0, 0, 0.85) url('/img/login_form_bg.png') no-repeat center;
    background-size: cover;
  }

  .features-box {
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 12px;
    background: rgba(0, 0, 0, 0.51);
  }

  .price-card {
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 12px;
    background: rgba(0, 0, 0, 0.51);
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-6px);
      border-color: rgba(115, 198, 4, 0.4);
      box-shadow: 0 8px 30px rgba(115, 198, 4, 0.1);
    }
  }

  .price-card--popular {
    border: 2px solid #73c604;
     background: rgba(255, 255, 255, 0.10);

    &:hover {
      border-color: #73c604;
      box-shadow: 0 8px 30px rgba(115, 198, 4, 0.25);
    }
  }

  .popular-badge {
    position: absolute;
    top: -13px;
    left: 50%;
    transform: translateX(-50%);
    background: #73c604;
    color: #000;
    font-weight: 700;
    font-size: 12px;
    padding: 3px 16px;
    border-radius: 4px;
    white-space: nowrap;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  /* deal card sits after .price-card so its border/background win on equal specificity */
  .price-card--deal {
    border: 2px solid #ffb020;
    background: rgba(255, 176, 32, 0.08);

    &:hover {
      border-color: #ffb020;
      box-shadow: 0 8px 30px rgba(255, 176, 32, 0.25);
    }
  }

  .deal-badge {
    position: absolute;
    top: -13px;
    left: 50%;
    transform: translateX(-50%);
    background: #ffb020;
    color: #000;
    font-weight: 700;
    font-size: 12px;
    padding: 3px 16px;
    border-radius: 4px;
    white-space: nowrap;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    animation: deal-pulse 3s ease-in-out infinite;
  }

  @keyframes deal-pulse {
    0%,
    100% {
      box-shadow: 0 0 0 0 rgba(255, 176, 32, 0.45);
    }
    50% {
      box-shadow: 0 0 0 9px rgba(255, 176, 32, 0);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .deal-badge {
      animation: none;
    }
  }

  .was-price {
    color: rgba(255, 255, 255, 0.45);
    font-size: 20px;
    font-weight: 600;
    margin-right: 12px;
    text-decoration-color: #ffb020;
  }

  .deal-period {
    margin-left: 6px;
  }

  .renews-note {
    color: #ffb020;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.3px;
  }

  .price-large {
    font-size: 48px;
    font-weight: 800;
    line-height: 1;
  }

  .price-decimal {
    font-size: 28px;
    font-weight: 700;
    vertical-align: super;
    line-height: 1;
  }

  .subscribe-btn {
    min-width: 160px !important;
    font-weight: 600;
    letter-spacing: 0.5px;
    background: linear-gradient(170.95deg, #84A92B 13.15%, #2F681F 93.36%);

  }
</style>
